import { createStylesheet } from '@shared/dom/index.ts';
import { sendEvent } from '@shared/hooks/useWindow';
import { proxyBlobContainer } from '@shared/utils/env';

import { EventType } from '@/constants';
import type { IToastContext } from '@/context/toast';

const colorRegexp = new RegExp('--primary: (?<color>#[a-zA-Z0-9]*?);');
const LOAD_TIMEOUT_MS = 5000;

export function loadTenantStyles(
  cssUrl: string,
  toast: IToastContext,
  errMsg: string,
  // EWM-548: optional hook invoked when the stylesheet fails to load (e.g. expired SAS -> 403),
  // so callers can transparently re-fetch a fresh tenant CSS URL.
  onError?: () => void,
) {
  createStylesheet(
    cssUrl,
    () => {
      sendEvent(EventType.CSS_LOADED, true);
    },
    () => {
      toast.showToast(errMsg, 'warning');
      onError?.();
    },
  );
}

export const extractCssColor = (sheet: CSSStyleSheet | undefined): string | undefined =>
  Array.from(sheet?.rules || [])
    ?.find(({ cssText }) => cssText.match(colorRegexp))
    ?.cssText.match(colorRegexp)?.groups?.color;

export const extractStylesheet = (
  sheets: CSSStyleSheet[],
  sheet: string,
): CSSStyleSheet | undefined => Array.from(sheets).find(({ href }) => href?.includes(sheet));

/**
 * Reads the tenant's --primary color from their CSS URL without permanently applying it to the
 * document. Injects a temporary <link> into the document head, reads --primary via
 * getComputedStyle (works cross-origin without CORS headers), then immediately removes the link.
 */
export function fetchTenantHighlightColor(cssUrl: string): Promise<string | undefined> {
  if (!cssUrl?.trim()) return Promise.resolve(undefined);

  const proxyUrl = proxyBlobContainer(cssUrl);

  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = proxyUrl;

    let settled = false;
    const finish = (color?: string) => {
      if (settled) return;
      settled = true;
      try {
        document.head.removeChild(link);
      } catch {
        /* already removed */
      }
      resolve(color || undefined);
    };

    link.onload = () => {
      const color = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
      finish(color);
    };
    link.onerror = () => finish(undefined);

    document.head.appendChild(link);
    setTimeout(() => finish(undefined), LOAD_TIMEOUT_MS);
  });
}
